import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, Button, FormField, Input } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  useCreateSpvTrust,
  useSpvTrust,
  useUpdateSpvTrust,
} from "@/hooks/capitalMarkets/useSpvTrusts";
import type { SpvCreate } from "@/types/spvPool";

const EMPTY_FORM: SpvCreate = {
  trustName: "",
  stateOfFormation: "",
  formationDate: "",
  ein: "",
  trustCompanyId: null,
  indentureTrusteeId: null,
  backupServicerId: null,
  primaryPlacementAgentId: null,
  governingLawState: "",
  notes: "",
};

export function TrustFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const toast = useToast();

  const trustQuery = useSpvTrust(id);
  const create = useCreateSpvTrust();
  const update = useUpdateSpvTrust(id ?? "");

  const [form, setForm] = useState<SpvCreate>(EMPTY_FORM);

  useEffect(() => {
    if (isEdit && trustQuery.data) {
      const t = trustQuery.data;
      setForm({
        trustName: t.trustName,
        stateOfFormation: t.stateOfFormation,
        formationDate: t.formationDate?.slice(0, 10) ?? "",
        ein: "",
        trustCompanyId: t.trustCompanyId,
        indentureTrusteeId: t.indentureTrusteeId,
        backupServicerId: t.backupServicerId,
        primaryPlacementAgentId: t.primaryPlacementAgentId,
        governingLawState: t.governingLawState,
        notes: t.notes ?? "",
      });
    }
  }, [isEdit, trustQuery.data]);

  const set = <K extends keyof SpvCreate>(k: K, v: SpvCreate[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const pending = create.isPending || update.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const action = isEdit
      ? update.mutateAsync(form)
      : create.mutateAsync(form);
    action
      .then((trust) => {
        toast.success(isEdit ? "Trust updated." : "Trust created.");
        navigate(`/capital-markets/trusts/${trust.id}`);
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <>
      <PageHeader
        eyebrow="Capital Markets"
        title={isEdit ? "Edit SPV Trust" : "New SPV Trust"}
        description="Define the SPV trust entity. Service providers can be assigned from the detail page."
      />
      <form onSubmit={handleSubmit} className="flex flex-col gap-space-4">
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
            <FormField label="Trust name" required className="md:col-span-2">
              <Input
                value={form.trustName}
                onChange={(e) => set("trustName", e.target.value)}
                required
              />
            </FormField>
            <FormField label="State of formation" required>
              <Input
                maxLength={2}
                value={form.stateOfFormation}
                onChange={(e) =>
                  set(
                    "stateOfFormation",
                    e.target.value.toUpperCase().slice(0, 2),
                  )
                }
                required
              />
            </FormField>
            <FormField label="Governing law state" required>
              <Input
                maxLength={2}
                value={form.governingLawState}
                onChange={(e) =>
                  set(
                    "governingLawState",
                    e.target.value.toUpperCase().slice(0, 2),
                  )
                }
                required
              />
            </FormField>
            <FormField label="Formation date" required>
              <Input
                type="date"
                value={form.formationDate}
                onChange={(e) => set("formationDate", e.target.value)}
                required
              />
            </FormField>
            <FormField
              label="EIN"
              helperText="Stored encrypted; masked on read."
            >
              <Input
                value={form.ein ?? ""}
                onChange={(e) => set("ein", e.target.value || null)}
                placeholder="12-3456789"
              />
            </FormField>
            <FormField label="Notes" className="md:col-span-2">
              <Input
                value={form.notes ?? ""}
                onChange={(e) => set("notes", e.target.value || null)}
              />
            </FormField>
          </div>
        </Card>
        <div className="flex items-center justify-end gap-space-2">
          <Button
            type="button"
            variant="text"
            onClick={() => navigate("/capital-markets/trusts")}
          >
            Cancel
          </Button>
          <Button type="submit" loading={pending}>
            {isEdit ? "Save changes" : "Create trust"}
          </Button>
        </div>
      </form>
    </>
  );
}
